import React, { useState, useEffect, useRef } from "react";
import Dropzone from "../dropzone/Dropzone";
import "./Upload.css";
import Progress from "../progress/Progress";
import Button from '@material-ui/core/Button';
import PublishIcon from '@material-ui/icons/Publish';
import Auth from '../../../../services/auth';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { acceptedFileTypes } from "../uploadHelper";
import ReactGA from 'react-ga4';
import { uploadCodingDocument, getUploadPolicy } from "../../../../services/transcriptionService";
import { findAlreadyUploadedFiles } from "../../../../services/priorUploadService";
import { Checkbox, FormControlLabel, LinearProgress, makeStyles, Typography } from "@material-ui/core";
import AlreadyUploadedDialog from "./AlreadyUploadedDialog";
import { useSnackbar } from "notistack";
import WorkTypeSelector from "../../../WorkTypeSelector";
import PubSub from 'pubsub-js';

const auth = new Auth();

const useStyles = makeStyles(() => ({
    errorText: {
        color: '#EF4444',
    },
}));

// Coding Upload's own extra option -- "SUPPORTING DOCUMENTS" has no matching per-WorkType Aspose
// template, so it has no meaning for Transcription Upload's job-creation path (it would always
// fail to find a template). Only the default (Coding) uploadFn gets it.
const codingAdditionalWorktypes = [{Wt: "0", Description:"SUPPORTING DOCUMENTS"}]

// Transcription intake rejects a work type selection it cannot resolve with HTTP 400 {Reason}.
// Plain-language text per reason; anything else keeps the existing generic message. Every row of
// the list is accepted (spec transcription-worktype-parity.md): a row without a usable template
// makes a Status 3 "Verify WorkType" job, so intake no longer answers MissingTemplate, and
// InvalidWorkType now only means the selection no longer matches the account's setup.
export const workTypeRejectionMessages = {
    UnknownWorkType: 'Upload rejected: the selected work type was not found for this account. Refresh the page, choose the work type again and retry.',
    AmbiguousWorkType: 'Upload rejected: the selected work type matches more than one work type set up for this account. Contact support to correct the work type setup.',
    InvalidWorkType: 'Upload rejected: the selected work type no longer matches this account\'s work type setup. Refresh the page, choose the work type again and retry.',
};

const rejectionReason = (response) => {
    if (!response || response.status !== 400 || !response.data || typeof response.data !== 'object') return null;
    const reason = response.data.Reason || response.data.reason;
    return Object.prototype.hasOwnProperty.call(workTypeRejectionMessages, reason) ? reason : null;
};

// Transcription intake returns HTTP 503 {Reason: NotReady | TemplateStoreUnavailable} when it
// cannot check work types right now. Not a work type problem: a plain "try again later".
export const transcriptionUnavailableMessage = 'Transcription Upload is temporarily unavailable. Try again later.';
const temporarilyUnavailableReasons = ['NotReady', 'TemplateStoreUnavailable'];

const isTemporarilyUnavailable = (response) => {
    if (!response || response.status !== 503 || !response.data || typeof response.data !== 'object') return false;
    return temporarilyUnavailableReasons.includes(response.data.Reason || response.data.reason);
};

// "Corrected report" (Coding Upload only): every file of the submission is a corrected op note. The
// server stores the choice on the upload's receipt; OCR runs as usual and the jobs get the CORRECTED
// JobId prefix. Coding intake answers 503 {Reason: CorrectedNotReady} (nothing stored) until the
// database is ready for it.
export const correctedUnavailableMessage = 'Corrected uploads are not available yet. Nothing was received; upload without "Corrected report" or try again later.';
export const correctedReportHelp = 'Applies to every file in this upload: each is processed as usual and its jobs are created as corrected reports.';

const isCorrectedNotReady = (response) => !!response && response.status === 503 && !!response.data && typeof response.data === 'object'
    && (response.data.Reason || response.data.reason) === 'CorrectedNotReady';

// "Preview only (don't create a job)" (Transcription Upload, Stage, allowlisted users): offered only
// when the WebApi's upload policy says this user may preview (TranscriptionPreviewAllowed), sent as
// preview:true only when ticked. The upload runs exactly as a real one but creates nothing; its
// history row shows "Preview ready" and the Word document the job would have had. An older WebApi
// never says so, and the WebApi refuses preview for anyone else (403 { Reason: PreviewNotAllowed }).
export const PREVIEW_LABEL = "Preview only (don't create a job)";
export const previewHelp = 'Runs the whole upload process for every file in this upload, but creates no job, patient, Inbox document or SharePoint copy. The upload history shows what would have happened, with the Word document the job would have had.';
export const previewNotAllowedMessage = 'Preview is not available to you here. Nothing was received; upload without "Preview only" or contact the owner.';

const isPreviewNotAllowed = (response) => !!response && response.status === 403 && !!response.data && typeof response.data === 'object'
    && (response.data.Reason || response.data.reason) === 'PreviewNotAllowed';

// selectByKey (Transcription Upload): the picker is keyed by the exact row (WorkTypeId, else list
// position -- Name is not unique), shows "LABEL (Wt)" only where labels collide, and posts the
// row's own Description plus workTypeKey = the row's Name ("900[01]-2"). Off (Coding Upload): picker, labels and body unchanged.
const Upload = ({ caption, uploadFn = uploadCodingDocument, additionalWorktypes = codingAdditionalWorktypes, workflow = "SnChart Coding", selectByKey = false }) => {
    const classes = useStyles();
    const generation = useRef(0);
    const accountEpoch = useRef(0);
    const mounted = useRef(true);
    const busy = useRef(false);
    const [availability, setAvailability] = useState({ enabled: false, message: 'Checking upload availability.' });
    // Transcription "Preview only": whether the WebApi says this user may preview here (re-read with the policy).
    const [previewAllowed, setPreviewAllowed] = useState(false);
    const [preview, setPreview] = useState(false);
    // The latest policy's answer, readable right after refreshPolicy resolves (state updates later).
    const previewPolicy = useRef(false);
    const refreshPolicy = async () => {
        const request = ++generation.current;
        const account = auth.getActiveAccount();
        try {
            const response = await getUploadPolicy(account);
            if (!mounted.current || request !== generation.current || account !== auth.getActiveAccount()) return false;
            const policy = (response.data.Policies || []).find(item => item.Workflow === workflow);
            const enabled = !!policy && policy.UploadEnabled === true;
            const allowsPreview = enabled && workflow === 'Transcription' && response.data.TranscriptionPreviewAllowed === true;
            previewPolicy.current = allowsPreview;
            setPreviewAllowed(allowsPreview);
            if (!allowsPreview) setPreview(false);
            setAvailability({ enabled, message: enabled ? '' : 'Uploads are disabled for this account. Selected files are preserved.' });
            return enabled;
        } catch (_) {
            if (mounted.current && request === generation.current) {
                previewPolicy.current = false;
                setPreviewAllowed(false);
                setAvailability({ enabled: false, message: 'Upload availability could not be checked. Refresh availability to retry.' });
            }
            return false;
        }
    };
    useEffect(() => {
        mounted.current = true;
        refreshPolicy();
        const changing = PubSub.subscribe('AccountChanging', () => {
            ++generation.current;
            ++accountEpoch.current;
            busy.current = false; setLoading(false); setFailedUpload(false); setUploadErrorMessage(null); setAlreadyUploaded(null); setCorrected(false);
            previewPolicy.current = false; setPreviewAllowed(false); setPreview(false);
            setAvailability({ enabled: false, message: 'Checking upload availability.' });
            setState(previous => ({ ...previous, files: [], workType: null, workTypeRow: null, successfullUploaded: false }));
        });
        const changed = PubSub.subscribe('AccountChanged', refreshPolicy);
        window.addEventListener('focus', refreshPolicy);
        return () => { mounted.current = false; ++generation.current; ++accountEpoch.current; PubSub.unsubscribe(changing); PubSub.unsubscribe(changed); window.removeEventListener('focus', refreshPolicy); };
    }, [workflow]);
    const [state, setState] = useState({
        files: [],
        uploading: false,
        uploadProgress: {},
        successfullUploaded: false,
        workType:null,
        workTypeRow:null
    });
    const [failedUpload, setFailedUpload] = useState(false);
    // "Corrected report" for this submission (Coding Upload only).
    const [corrected, setCorrected] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadErrorMessage, setUploadErrorMessage] = useState(null);
    // { hits: [{ file, uploads }], scope } while the "already uploaded" dialog is open.
    const [alreadyUploaded, setAlreadyUploaded] = useState(null);
    // The last list shown, so the dialog keeps its content while it fades out.
    const shownHits = useRef([]);
    if (alreadyUploaded) shownHits.current = alreadyUploaded.hits;
    const { enqueueSnackbar } = useSnackbar();
    const onFilesAdded = (files) => {
    if (files.some(file => file.size > 80 * 1024 * 1024)) {
        setUploadErrorMessage('Each file must be 80 MiB or smaller. Larger files were not added.');
    }
    files = files.filter(x => acceptedFileTypes.includes(x.type) && x.size <= 80 * 1024 * 1024);
    setState((prevState) => ({...prevState,
            files: prevState.files.concat(files),
        }));
    };

  const uploadFiles = async (account, scope, files = state.files) => {
    const batchSize = 4;

    const fileChunks = [];
    for (let i = 0; i < files.length; i += batchSize) {
        fileChunks.push(files.slice(i, i + batchSize));
    }

    for (const chunk of fileChunks) {
        if (scope !== accountEpoch.current || account !== auth.getActiveAccount()) throw new Error("Account changed");
        const uploadPromises = chunk.map(file => sendRequest(file, account, scope));
        await Promise.all(uploadPromises);
    }
    ReactGA.event({
        category: 'CodingUploadView',
        action: 'BulkUpload',
        label: auth.getUserType()
    });
  };

    // Coding Upload only: before sending, ask whether this account already uploaded any of the
    // selected files (same SHA-256, last 30 days). Transcription Upload is unchanged.
    const warnAlreadyUploaded = workflow === "SnChart Coding" && uploadFn === uploadCodingDocument;
    // Coding Upload only, like the warning above; Transcription Upload is unchanged.
    const offerCorrected = workflow === "SnChart Coding" && uploadFn === uploadCodingDocument;
    // Transcription Upload only, and only when the WebApi says this user may preview.
    const offerPreview = workflow === "Transcription" && selectByKey && previewAllowed;
    // What this submission sends: fixed when Upload is pressed, re-checked against the fresh policy.
    const previewSubmission = useRef(false);

    const handleUpload = () => startUpload(null);

    // confirmedFiles: what the user chose in the "already uploaded" dialog; sent without asking again.
    const startUpload = async (confirmedFiles) => {
        if (busy.current) return;
        busy.current = true;
        const scope = accountEpoch.current;
        const account = auth.getActiveAccount();
        const current = () => mounted.current && scope === accountEpoch.current && account === auth.getActiveAccount();
        setLoading(true);
        const wantsPreview = offerPreview && preview;
        try {
            const policyAllows = await refreshPolicy();
            if (!policyAllows || !current()) return;
            previewSubmission.current = wantsPreview;
            // Never send a preview the WebApi no longer offers, and never quietly send it as a real upload.
            if (wantsPreview && !previewPolicy.current) {
                setUploadErrorMessage(previewNotAllowedMessage);
                return;
            }
            if (!confirmedFiles && warnAlreadyUploaded) {
                const hits = await findAlreadyUploadedFiles(account, state.files);
                if (!current()) return;
                if (hits.length > 0) {
                    ReactGA.event({ category: 'CodingUploadView', action: 'AlreadyUploadedWarningShown', label: auth.getUserType ? auth.getUserType() : undefined });
                    setAlreadyUploaded({ hits, scope });
                    return;
                }
            }
            await uploadFiles(account, scope, confirmedFiles || state.files);
            if (!current()) return;
            PubSub.publish('BatchCodingUploadStatusRefresh');
            setState(previous => ({ ...previous, successfullUploaded: true }));
        } catch (_) {
            if (current()) { enqueueSnackbar('Failed to upload files.', { variant: 'error' }); setFailedUpload(true); }
        } finally {
            if (current()) { busy.current = false; setLoading(false); }
        }
    };

    // choice: 'all' uploads every selected file again, 'new' drops the already-uploaded files
    // from the selection and uploads the rest, 'cancel' sends nothing and keeps the selection.
    const resolveAlreadyUploaded = (choice) => {
        const pending = alreadyUploaded;
        setAlreadyUploaded(null);
        if (!pending || pending.scope !== accountEpoch.current) return;
        ReactGA.event({ category: 'CodingUploadView', action: choice === 'all' ? 'AlreadyUploadedOverride' : choice === 'new' ? 'AlreadyUploadedSkipped' : 'AlreadyUploadedCancelled' });
        if (choice !== 'all' && choice !== 'new') return;
        const repeated = pending.hits.map(hit => hit.file);
        const files = choice === 'all' ? state.files : state.files.filter(file => !repeated.includes(file));
        if (choice === 'new') setState(previous => ({ ...previous, files }));
        if (files.length > 0) startUpload(files);
    };

    const sendRequest = async (file, account, scope) => {
        try {
          const formData = new FormData();
          formData.append("file", file, file.name);
          formData.append("account", auth.getActiveAccount());
          formData.append("workType", state.workType);


          const fileBase64 = await convertFileToBase64(file);

          const jsonPayload = {
              originalClinicalDocument: fileBase64,
              originalClinicalFileName: account + " " + file.name,
              patientRecords: [
                  {
                      demographics: {
                          patientFirstName: "",
                          patientLastName: "",
                          gender: "",
                          dateOfBirth: "",
                          mrn: "",
                          dateOfService: "",
                          providerFirstName: "",
                          providerLastName: "",
                          stateSeenIn: "",
                          pageNumber: "1",
                          totalPages: "1",
                      },
                      accountName: account,
                      workType: selectByKey ? state.workTypeRow.Description : state.workType,
                      documents: {
                          ocrTextDocument: "",
                      },
                  },
              ],
          };
          if (selectByKey) {
              // The row's own key, so rows sharing a Description stay distinct server-side.
              jsonPayload.patientRecords[0].workTypeKey = state.workTypeRow.Name;
          }
          // Sent only when ticked: an unticked upload's body is exactly as before.
          if (offerCorrected && corrected) {
              jsonPayload.isCorrected = true;
          }
          // "Preview only": likewise sent only when ticked (and offered), for every file of the submission.
          if (previewSubmission.current) {
              jsonPayload.preview = true;
          }


          // Send the JSON payload
          if (scope !== accountEpoch.current || account !== auth.getActiveAccount()) throw new Error("Account changed");
          const result = await uploadFn(jsonPayload);
          if (!result.data) {
              throw new Error("file failed to upload");
          }
        } catch (error) {
            if (mounted.current && scope === accountEpoch.current && account === auth.getActiveAccount() && error && error.response) {
                const reason = selectByKey ? rejectionReason(error.response) : null;
                setUploadErrorMessage(reason ? workTypeRejectionMessages[reason]
                    : selectByKey && isTemporarilyUnavailable(error.response) ? transcriptionUnavailableMessage
                    : offerCorrected && isCorrectedNotReady(error.response) ? correctedUnavailableMessage
                    : isPreviewNotAllowed(error.response) ? previewNotAllowedMessage
                    : 'Upload failed. Refresh upload history before retrying.');
            }
            throw new Error("file failed to upload");
        }
    };

  // Helper function to convert file to Base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const base64String = reader.result.split(",")[1]; // Extract Base64 part
            if (base64String) {
                resolve(base64String);
            } else {
                reject(new Error("Failed to extract Base64 string"));
            }
        };
        reader.onerror = (error) => reject(new Error(`FileReader error: ${error.message}`));
        reader.readAsDataURL(file);
    });
  };

  const renderProgress = (file) => {
    const uploadProgress = state.uploadProgress ? state.uploadProgress[file.name] : 0;
      if (loading) {
        return (
            <LinearProgress />
        )
    }
      if (!loading && state.successfullUploaded) {
      return (
        <div className="ProgressWrapper">
              <Progress progress={uploadProgress ? uploadProgress.percentage : 0} />
              <div style={{ color: 'rgb(34 197 94)' }}>
                  <CheckCircleOutlineIcon />
              </div>
        </div>
      );
      }
      if (!loading && failedUpload) {
          return (
              <div className="ProgressWrapper">
                  <Progress progress={uploadProgress ? uploadProgress.percentage : 0} />
                  <div style={{ color: 'rgb(221 35 32)' }}>
                      <HighlightOffIcon />
                  </div>
              </div>
          );
      }
  };

  const renderActions = () => {
    if (state.successfullUploaded || failedUpload) {
      return (
        <Button
              onClick={() => {
                  setState({ ...state, files: [], successfullUploaded: false, workType:null, workTypeRow:null})
                  setCorrected(false)
                  setPreview(false)
              }
          }
          color="primary"
          variant="contained"
        >
          Clear
        </Button>
      );
    } else {
      return (
        <Button
          color="primary"
          disabled={!availability.enabled || state.files.length < 1 || loading || !state.workType || (selectByKey && !state.workTypeRow)}
          onClick={handleUpload}
          variant="contained"
          startIcon={<PublishIcon />}
        >
          Upload
        </Button>
      );
    }
  };

  const handleWorkTypeChange = (workType, row) => {
      setState(selectByKey ? { ...state, workType:workType, workTypeRow:row || null } : { ...state, workType:workType });
  }

  return (
      <div className="Upload">
      {availability.message && <Typography role="status">{availability.message}</Typography>}
      <Button onClick={refreshPolicy} disabled={loading}>Refresh availability</Button>
      <div>
              <WorkTypeSelector account={auth.getActiveAccount()} selectedWorkType={state.workType } onChange={handleWorkTypeChange} keyProperty="Description" keyByRow={selectByKey} disambiguateLabels={selectByKey} additionalWorktypes={additionalWorktypes } />
      </div>
      {offerPreview && (
          <div>
              <FormControlLabel
                  control={<Checkbox color="primary" checked={preview} onChange={(event) => setPreview(event.target.checked)}
                      disabled={loading || state.successfullUploaded} inputProps={{ 'aria-describedby': preview ? 'preview-only-help' : undefined, 'data-testid': 'preview-only' }} />}
                  label={PREVIEW_LABEL} />
              {preview && <Typography id="preview-only-help" variant="caption" display="block">{previewHelp}</Typography>}
          </div>
      )}
      {offerCorrected && (
          <div>
              <FormControlLabel
                  control={<Checkbox color="primary" checked={corrected} onChange={(event) => setCorrected(event.target.checked)}
                      disabled={loading || state.successfullUploaded} inputProps={{ 'aria-describedby': corrected ? 'corrected-report-help' : undefined }} />}
                  label="Corrected report" />
              {corrected && <Typography id="corrected-report-help" variant="caption" display="block">{correctedReportHelp}</Typography>}
          </div>
      )}
      <div className="Content">
        <div>
          <Dropzone
            caption={caption}
            onFilesAdded={onFilesAdded}
            disabled={!availability.enabled || loading || state.successfullUploaded}
          />
        </div>
        <div className="Files">
          {state.files.map((file) => {
            return (
              <div key={file.name} className="Row">
                <span className="Filename">{file.name}</span>
                {renderProgress(file)}
              </div>
            );
          })}
        </div>
          </div>
          {uploadErrorMessage ? (
              <Typography class={classes.errorText} variant="subtitle2" gutterBottom>
                  {uploadErrorMessage}
              </Typography>) : null}
      <div className="Actions">{renderActions()}</div>
      <AlreadyUploadedDialog
          hits={shownHits.current}
          open={!!alreadyUploaded}
          hasNewFiles={shownHits.current.length < state.files.length}
          onChoose={resolveAlreadyUploaded} />
    </div>
  );
};

export default Upload;



// WEBPACK FOOTER //
// ./src/components/Common/CommonUpload/upload/Upload.js